import React, {
	useEffect,
	useState,
	useRef,
	useContext,
	useCallback,
} from 'react';
import withAppContext from '../../hooks/withAppContext';
import cn from 'classnames';
import { IBaseElementProps } from '../BasicElement';
import Icon from '../Icon';
import debounce from 'lodash.debounce';
import Loading from '../Loading';

export interface IVHSSearchPopularContent {
	PCUrl: string;
	PCLabel: string;
}
export interface IVHSSearchProps extends IBaseElementProps {
	/**
	 * Search Placeholder
	 */
	placeholder?: string;
	/**
	 * Link to the home page
	 */
	homeURL?: string;
	popularContent?: IVHSSearchPopularContent[];
	closeSearchMobile?: Function;
}

const VHSSearch: React.FC<IVHSSearchProps> = withAppContext(
	({ context, placeholder, homeURL, popularContent, closeSearchMobile }) => {
		const { Api } = useContext(context);
		const [search, setSearch] = useState(null);
		const [busy, setBusy] = useState(false);
		const [dropdownVisible, setDropdownVisible] = useState(false);
		const [{ search_results, history_results }, setSearchResults] =
			useState({
				history_results: [],
				search_results: [],
			});

		const inputRef = useRef<HTMLInputElement>(null);
		const dropdownRef = useRef<HTMLDivElement>(null);
		const formRef = useRef<HTMLFormElement>(null);

		const fetchResults = useCallback(
			debounce((search) => {

				if (!search && history_results.length > 0) {
					// no need to fetch if empty string and history results already exist in state
					setBusy(false);
					setSearchResults({
						history_results,
						search_results: [],
					});
				} else {
					Api.get_results_for_search({ s: search })
						.then((results) => {
							setSearchResults(results);
						})
						.finally(() => setBusy(false));
				}
			}, 700),
			[history_results],
		);

		useEffect(() => {
			setBusy(true);
			// set busy state here before debounced fetch to avoid getting "No results found" from search state update before debounced function runs
			fetchResults(search);
		}, [search]);

		const handleDropdownClose = () => {
			setDropdownVisible(false);
			if (window.innerWidth < 576) closeSearchMobile(false); // mobile search has its own state, so we need to close it when the dropdown closes
		};

		useEffect(() => {
			if (dropdownVisible) {

				const lastDropdownItem = document.querySelector(
					'.vhs-search__popular-item:last-child',
				) as HTMLDivElement;

				const handleClickOutside = (event: MouseEvent) => {
					if (
						dropdownRef.current &&
						!dropdownRef.current.contains(event.target as Node) &&
						inputRef.current &&
						!inputRef.current.contains(event.target as Node)
					) {
						handleDropdownClose();
					}
				};

				const handleEscKey = (event: KeyboardEvent) => {
					if (event.key === 'Escape') {
						handleDropdownClose();
					}
				};

				const handleLastDropdownItem = (event: KeyboardEvent) => {
					handleDropdownClose();
				};

				lastDropdownItem.addEventListener('keydown', handleLastDropdownItem);
				document.addEventListener('keydown', handleEscKey);
				document.addEventListener('click', handleClickOutside);

				return () => {
					document.removeEventListener('click', handleClickOutside);
					document.removeEventListener('keydown', handleEscKey);
					lastDropdownItem.removeEventListener('keyup', handleLastDropdownItem);
				};
			}
		}, [dropdownVisible, handleDropdownClose]);

		return (
			<form
				className={cn('vhs-search__form')}
				action={homeURL}
				method="get"
				onFocus={() => setDropdownVisible(true)}
				ref={formRef}
			>
				<input
					name="s"
					type="text"
					className="vhs-search__input"
					placeholder={placeholder}
					aria-label={placeholder}
					id="search"
					onChange={(e) => setSearch(e.target.value)}
					value={search || ''}
					ref={inputRef}
					autoComplete="new-password"
				/>
				<label htmlFor="search">{placeholder}</label>
				<button type="submit" className="vhs-search__submit">
					<Icon name="magnifying-glass" />
					<span className="visually-hidden">{placeholder}</span>
				</button>
				{dropdownVisible && (
					<div className="vhs-search__results" ref={dropdownRef}>
						<div className="vhs-search__search-results">
							{Array.isArray(search_results) &&
							search_results.length > 0 ? (
								search_results.map(({ title, link }, index) => (
									<a
										className="vhs-search__search-item"
										href={link}
										key={index}
										onClick={(e) => {
											e.preventDefault();
											window.location.href = link;
											handleDropdownClose();
										}}
									>
										{title}
									</a>
								))
							) : (
								<div className={`vhs-search__search-item`}>
									{busy ? (
										<Loading />
									) : search?.length > 0 ? (
										'No results found'
									) : (
										'Start typing to search'
									)}
								</div>
							)}
						</div>
						{Array.isArray(history_results) &&
							history_results.length > 0 && (
								<div className="vhs-search__search-history">
									<div className="vhs-search__history-heading">
										Previous Searches
									</div>
									<div className="vhs-search__history-items">
										{history_results.map(
											({ title, link }, index) => (
												<a
													className="vhs-search__history-item"
													href={link}
													key={index}
													onClick={(e) => {
														handleDropdownClose();
													}}
												>
													<Icon name="history" />
													{title}
												</a>
											),
										)}
									</div>
								</div>
							)}
						{Array.isArray(popularContent) &&
							popularContent.length > 0 && (
								<div className="vhs-search__popular-content">
									<div className="vhs-search__popular-heading">
										Popular Content
									</div>
									<div className="vhs-search__popular-items">
										{popularContent?.map(
											({ PCLabel, PCUrl }, index) => (
												<a
													className="vhs-search__popular-item"
													href={PCUrl}
													key={index}
													onClick={(e) => {
														handleDropdownClose();
													}}
												>
													<Icon name="magnifying-glass" />
													{PCLabel}
												</a>
											),
										)}
									</div>
								</div>
							)}
					</div>
				)}
			</form>
		);
	},
);
export default VHSSearch;
